import { useState, useEffect, useRef } from 'react';
import { toast } from '@/hooks/use-toast';

type AudioPlaybackHookProps = {
  onPlaybackStarted?: () => void;
  onPlaybackEnded?: () => void;
};

export const useAudioPlayback = (props?: AudioPlaybackHookProps) => {
  const { onPlaybackStarted, onPlaybackEnded } = props || {};

  const [isPlaying, setIsPlaying] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Create audio element only once on mount
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.autoplay = true;
      audioRef.current.addEventListener('ended', handleAudioEnded);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleAudioEnded);
        // Store the current audio element reference
        const audio = audioRef.current;
        // Clear the ref first to prevent race conditions
        audioRef.current = null;
        // Then pause the detached audio element
        setTimeout(() => {
          try {
            audio.pause();
          } catch (e) {
            console.error('Error pausing audio during cleanup:', e);
          }
        }, 0);
      }
      // Clean up any existing blob URLs (moved to resetAudio to avoid race conditions)
    };
  }, []);

  // Handle audioUrl changes separately
  useEffect(() => {
    // Use a flag to track if this effect instance should continue
    let isEffectActive = true;

    const playAudio = async () => {
      if (audioUrl && audioRef.current && isEffectActive) {
        try {
          // First load then play to ensure the blob is fully loaded
          audioRef.current.src = audioUrl;
          audioRef.current.load();

          // Wait a small amount of time to ensure the audio is loaded
          await new Promise(resolve => setTimeout(resolve, 50));

          // Check if we should still continue
          if (!isEffectActive || !audioRef.current) return;

          await audioRef.current.play();

          if (isEffectActive) {
            setGeneratingAudio(false);
            setIsPlaying(true);
            onPlaybackStarted?.();
          }
        } catch (error) {
          console.error('Audio playback error:', error);
          if (isEffectActive) {
            setGeneratingAudio(false);
            setIsPlaying(false);
            toast({
              title: 'Error',
              description: 'Failed to play audio. Please try again.',
              variant: 'destructive',
            });
          }
        }
      }
    };

    playAudio();

    return () => {
      // Mark this effect instance as inactive
      isEffectActive = false;
    };
  }, [audioUrl, onPlaybackStarted]);

  const handleAudioEnded = () => {
    setIsPlaying(false);
    onPlaybackEnded?.();
  };

  const generateAudio = async (text: string, voiceId: string) => {
    if (!text || !voiceId) {
      toast({
        title: 'Error',
        description: 'Script or actor voice not available',
        variant: 'destructive',
      });
      return;
    }

    if (audioUrl) {
      return;
    }

    // Generate new audio
    setGeneratingAudio(true);

    try {
      const endpointUrl = import.meta.env.VITE_TEXT_TO_SPEECH_API_URL;
      const response = await fetch(endpointUrl, {
        method: 'post',
        body: JSON.stringify({
          text,
          voice_id: voiceId,
          tk: import.meta.env.VITE_VIDEOS_API_TOKEN
        }),
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate audio');
      }

      // Get the audio stream data
      const audioStream = await response.body;

      if (!audioStream) {
        throw new Error('Failed to get audio stream');
      }

      // Create a MediaSource instance
      const mediaSource = new MediaSource();
      const mediaUrl = URL.createObjectURL(mediaSource);

      // Set the audio URL early so we can start loading the MediaSource
      setAudioUrl(mediaUrl);

      // Wait for the MediaSource to open before appending data
      const sourceOpened = new Promise((resolve) => {
        mediaSource.addEventListener('sourceopen', () => resolve(true), { once: true });
      });

      // Read the stream and append data to the MediaSource
      const reader = audioStream.getReader();

      // Start the process of reading the stream and appending data
      sourceOpened.then(async () => {
        try {
          // Create a source buffer for audio
          const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
          let isFirstChunk = true;

          // Process data as it comes in
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              if (mediaSource.readyState === 'open') {
                mediaSource.endOfStream();
              }

              break;
            }

            // Wait for the source buffer to be ready before appending
            if (sourceBuffer.updating) {
              await new Promise(resolve => {
                sourceBuffer.addEventListener('updateend', resolve, { once: true });
              });
            }

            // Append the chunk to the source buffer
            sourceBuffer.appendBuffer(value);

            // Start playback as soon as we have the first chunk
            if (isFirstChunk && audioRef.current) {
              isFirstChunk = false;
              setGeneratingAudio(false);
              setIsPlaying(true);
              onPlaybackStarted?.();
            }
          }
        } catch (error) {
          console.error('Error processing audio stream:', error);

          setGeneratingAudio(false);
        }
      });
      // The useEffect that watches audioUrl will handle playing the audio
    } catch (error) {
      console.error('Error generating audio:', error);
      setGeneratingAudio(false);
      toast({
        title: 'Error',
        description: 'Failed to generate audio. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const resetAudio = () => {
    // Properly clean up existing resources
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Clean up MediaSource URL to prevent memory leaks
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    setAudioUrl(null);
    setIsPlaying(false);
  };

  // We no longer need playAudio and pauseAudio functions
  // as we're using native audio controls

  return {
    isPlaying,
    generatingAudio,
    audioRef,
    generateAudio,
    handleAudioEnded,
    resetAudio,
    audioUrl
  };
};
